import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Separator } from '../components/ui/separator';
import { FadeIn, FadeInLeft, FadeInRight, StaggerContainer, StaggerItem } from '../components/motion/FadeIn';
import { SectionLabel } from '../components/shared/SectionLabel';
import { CTASection } from '../components/shared/CTASection';
import { IMAGES } from '../lib/images';
import { CheckCircle2, ArrowRight, TrendingUp, DollarSign, BarChart3, Info, Zap, Award, Star } from 'lucide-react';



const HeroSection = () => (
  <section className="relative overflow-hidden pt-20 sm:pt-24 pb-14 sm:pb-16">
    <div className="absolute inset-0 -z-10" style={{ background: 'radial-gradient(1200px circle at 50% 0%, rgba(42,140,134,0.10), transparent 55%)' }} />
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8 text-center max-w-3xl">
      <FadeIn>
        <SectionLabel>Pricing</SectionLabel>
        <h1 className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">Simple Pricing. Serious ROI.</h1>
        <p className="mt-5 text-base md:text-lg text-[hsl(var(--sss-ink))]/70">One flat monthly fee. Unlimited patients. No per-patient charges. No hidden costs.</p>
      </FadeIn>
    </div>
  </section>
);

const PricingCard = () => {
  const features = [
    'Unlimited patient enrollments, add as many declined patients as you need',
    'Practice-branded patient portal with your logo, your colors, your brand',
    'Credit monitoring from top bureaus so patients get real score tracking',
    'Intelligent guided improvement with personalized action plans, not generic tips',
    'Onboarding and training included, we set everything up and train your team',
    'Ongoing support whenever you need it',
  ];
  return (
    <section className="py-8 sm:py-12">
      <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
        <div className="grid gap-10 lg:grid-cols-12 items-start">
          <FadeIn className="lg:col-span-7">
            <Card className="rounded-3xl border-2 border-[hsl(var(--sss-teal))]/20 bg-white shadow-[var(--shadow-lg)]">
              <CardContent className="p-8 sm:p-10">
                <div className="text-center mb-2"><span className="text-sm font-medium text-[hsl(var(--sss-teal))] uppercase tracking-wide">Practice Plan</span></div>
                <div className="text-center mb-6">
                  <p className="text-5xl font-bold text-[hsl(var(--sss-navy))]" data-testid="pricing-amount">$249</p>
                  <p className="text-sm text-muted-foreground mt-1">per month</p>
                </div>
                <Separator className="my-6" />
                <h3 className="text-sm font-semibold text-[hsl(var(--sss-navy))] mb-4">Everything included:</h3>
                <div className="space-y-3 mb-8">
                  {features.map((f, i) => (
                    <div key={i} className="flex gap-3 text-sm text-[hsl(var(--sss-ink))]/80"><CheckCircle2 className="h-4 w-4 text-[hsl(var(--sss-teal))] shrink-0 mt-0.5" /><span>{f}</span></div>
                  ))}
                </div>
                <div className="space-y-2">
                  <a href="https://approvemysmile.com/register/personal" target="_blank" rel="noopener noreferrer" className="block">
                    <Button data-testid="pricing-get-started-button" className="w-full bg-[hsl(var(--sss-coral))] hover:bg-[hsl(var(--sss-coral-2))] text-white rounded-xl h-12 text-base font-medium shadow-md">Get Started <ArrowRight className="ml-2 h-4 w-4" /></Button>
                  </a>
                  <Link to="/contact" className="block">
                    <Button data-testid="pricing-book-demo-button" variant="outline" className="w-full border-[hsl(var(--sss-navy))] text-[hsl(var(--sss-navy))] hover:bg-[hsl(var(--sss-navy))] hover:text-white rounded-xl h-11">Book a Demo First</Button>
                  </Link>
                </div>
              </CardContent>
            </Card>
          </FadeIn>
          <FadeInRight className="lg:col-span-5">
            <div className="space-y-4">
              <div className="rounded-2xl overflow-hidden shadow-[var(--shadow-md)] h-56 sm:h-72">
                <img src={IMAGES.confidentYoungMan} alt="Happy patient" className="w-full h-full object-cover" loading="lazy" />
              </div>
              <Card className="rounded-2xl border border-[hsl(var(--border))] bg-[hsl(var(--accent))] shadow-[var(--shadow-sm)]">
                <CardContent className="p-5 text-center">
                  <p className="text-3xl font-bold text-[hsl(var(--sss-teal))]">10x</p>
                  <p className="text-sm text-muted-foreground mt-1">ROI from a single recovered implant case</p>
                </CardContent>
              </Card>
            </div>
          </FadeInRight>
        </div>
      </div>
    </section>
  );
};

/* ─── PREMIUM UPGRADE ─── */
const PremiumSection = () => (
  <section className="py-16 sm:py-20">
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
      <FadeIn>
        <Card className="rounded-2xl border-2 border-[hsl(var(--sss-coral))]/20 bg-white shadow-[var(--shadow-md)] max-w-4xl mx-auto overflow-hidden">
          <div className="grid lg:grid-cols-2">
            <CardContent className="p-8">
              <div className="flex items-center gap-2 mb-3">
                <Star className="h-5 w-5 text-[hsl(var(--sss-coral))]" />
                <span className="text-xs font-semibold uppercase tracking-widest text-[hsl(var(--sss-coral))]">Premium Add-On</span>
              </div>
              <h3 className="text-2xl font-bold text-[hsl(var(--sss-navy))] mb-3">
                Supercharge Your Hottest Leads
              </h3>
              <p className="text-sm text-[hsl(var(--sss-ink))]/70 leading-relaxed mb-4">
                For your most qualified patients — the ones who showed up, wanted treatment, and were right on the edge of approval — unlock the full <span className="font-semibold">Grow</span> experience with AI-powered credit coaching.
              </p>
              <Separator className="my-4" />
              <p className="text-xs font-semibold text-[hsl(var(--sss-navy))] mb-3">Full Grow Access Includes:</p>
              <div className="space-y-2">
                {[
                  'AI Credit Agent personalized to each patient\'s profile',
                  'Step-by-step guidance on exactly how to improve their score',
                  'Actionable insights that update as their credit changes',
                  'Accelerated path to financing approval',
                ].map((item, i) => (
                  <div key={i} className="flex gap-3 text-sm text-[hsl(var(--sss-ink))]/80">
                    <Zap className="h-4 w-4 text-[hsl(var(--sss-coral))] shrink-0 mt-0.5" />{item}
                  </div>
                ))}
              </div>
            </CardContent>
            <div className="bg-[hsl(var(--sss-navy))] p-8 flex flex-col justify-center text-white">
              <Award className="h-8 w-8 text-[hsl(var(--sss-coral))] mb-4" />
              <h4 className="text-lg font-semibold mb-2">Why Premium?</h4>
              <p className="text-sm text-white/80 leading-relaxed mb-4">
                Patients with a personalized AI action plan are better equipped to understand and manage their credit profile <span className="text-[hsl(var(--sss-coral))] font-bold">than those monitoring alone</span>.
              </p>
              <p className="text-sm text-white/80 leading-relaxed mb-6">
                Target your highest-value declined cases — the patients most likely to convert — and give them every tool to get approved faster.
              </p>
              <Link to="/contact">
                <Button className="bg-[hsl(var(--sss-coral))] hover:bg-[hsl(var(--sss-coral-2))] text-white rounded-xl h-11 w-full">
                  Ask About Premium <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>
          </div>
        </Card>
      </FadeIn>
    </div>
  </section>
);

const ROISection = () => (
  <section className="py-16 sm:py-20 bg-[hsl(var(--secondary))]">
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8">
      <FadeIn className="text-center max-w-3xl mx-auto mb-8">
        <SectionLabel>Return on Investment</SectionLabel>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[hsl(var(--sss-navy))]">The Math Is Simple</h2>
        <p className="mt-4 text-base text-[hsl(var(--sss-ink))]/70">The average dental implant case is worth $3,000 to $30,000 or more. If Smile Score Solutions helps you recover just one additional case per year, the platform pays for itself many times over.</p>
      </FadeIn>
      <StaggerContainer className="grid gap-4 sm:grid-cols-3 max-w-3xl mx-auto">
        {[{icon:<DollarSign className="h-5 w-5"/>,stat:'$249/mo',sub:'= $2,988/year'},{icon:<TrendingUp className="h-5 w-5"/>,stat:'1 Case',sub:'= $3,000 to $30,000+'},{icon:<BarChart3 className="h-5 w-5"/>,stat:'1x–10x',sub:'ROI from a single recovered case'}].map((item, i) => (
          <StaggerItem key={i}>
            <Card className="rounded-2xl border border-[hsl(var(--border))] bg-white shadow-[var(--shadow-sm)] hover:shadow-[var(--shadow-md)] transition-shadow text-center">
              <CardContent className="p-6">
                <div className="h-10 w-10 rounded-xl bg-[hsl(var(--accent))] text-[hsl(var(--sss-teal))] flex items-center justify-center mx-auto mb-3">{item.icon}</div>
                <p className="text-2xl font-bold text-[hsl(var(--sss-navy))]">{item.stat}</p>
                <p className="text-sm text-muted-foreground mt-1">{item.sub}</p>
              </CardContent>
            </Card>
          </StaggerItem>
        ))}
      </StaggerContainer>
    </div>
  </section>
);

const TransparencySection = () => (
  <section className="py-16 sm:py-20">
    <div className="mx-auto max-w-[var(--grid-max)] px-4 sm:px-6 lg:px-8 max-w-3xl">
      <FadeIn>
        <SectionLabel>Transparency</SectionLabel>
        <h2 className="text-2xl sm:text-3xl font-semibold tracking-tight text-[hsl(var(--sss-navy))] mb-6">What You Should Know</h2>
        <div className="space-y-4">
          {['There are no long-term contracts, cancel anytime','There are no per-patient fees, enroll as many patients as you want','There is no cost to your patients for the educational platform','Results depend on patient engagement and individual financial circumstances. No specific outcome is guaranteed','We are a financial wellness platform. We provide education, monitoring, guided action steps, and tools to help patients understand their credit'].map((item, i) => (
            <div key={i} className="flex gap-3 text-sm sm:text-base text-[hsl(var(--sss-ink))]/80"><Info className="h-5 w-5 text-[hsl(var(--sss-teal))] shrink-0 mt-0.5" /><span>{item}</span></div>
          ))}
        </div>
      </FadeIn>
    </div>
  </section>
);

export default function PricingPage() {
  return (
    <div>
      <HeroSection />
      <PricingCard />
      <PremiumSection />
      <ROISection />
      <TransparencySection />
      <CTASection headline="Start Recovering Lost Cases Today" subheadline="Join the growing number of dental practices using Smile Score Solutions to turn declined patients into accepted cases." />
    </div>
  );
}
